import { useLingui } from '@lingui/react/macro';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import React, { useCallback } from 'react';
import type { LayoutScopeDescriptor } from '../common/layout-scope-descriptors';
import CommitOptionalNumberField from './commit-optional-number-field';
import HelpBubble from './help-bubble';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import { useCommonFabricData } from './use-common-fabric-data';

export interface ScopeFilterSectionProps {
  objects: fabric.Object[];
  editor: FabricEditor;
  /**
   * The slot dimension the active template type addresses, or `undefined` when
   * it has none (SCHEDULE/RESULTS render one of everything) — the section then
   * hides. Hooks still run first so this stays a stable component.
   */
  descriptor: LayoutScopeDescriptor | undefined;
}

/**
 * Shared body of the per-slot filter section. Sets `data[scopeKey]` on the
 * selected objects so an object scoped to a slot that isn't rendered
 * (`matchNumber` beyond the rendered matches, `teamNumber` beyond the group's
 * teams) is stripped at render time by the render service. All copy comes from the
 * descriptor; see `SlotFilterSection` for the wrapper that picks one.
 */
const ScopeFilterSection: React.FC<ScopeFilterSectionProps> = ({ objects, editor, descriptor }) => {
  const { t } = useLingui();
  // `?? 'matchNumber'` is inert — the component returns null when there is no
  // descriptor. It only keeps the hook call unconditional.
  const scopeValue = useCommonFabricData<number>(objects, descriptor?.scopeKey ?? 'matchNumber', editor);

  const refitPlaceholders = useCallback(() => {
    const facade = editor.placeholders;
    if (!facade) return;
    for (const obj of objects) {
      if (facade.isPlaceholder(obj)) facade.refit(obj as fabric.Group);
    }
  }, [editor, objects]);

  if (!descriptor || objects.length === 0) return null;

  return (
    <InspectorSection title={t(descriptor.filterSectionTitle)}>
      <InspectorRow label={t(descriptor.filterFieldLabel)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CommitOptionalNumberField
              value={scopeValue.isMixed ? undefined : scopeValue.value}
              placeholder={scopeValue.isMixed ? t`(разное)` : '—'}
              min={1}
              onCommit={(v) => {
                scopeValue.setValue(v);
                refitPlaceholders();
              }}
            />
          </div>
          <HelpBubble content={descriptor.filterHelp} />
        </div>
      </InspectorRow>
    </InspectorSection>
  );
};

export default ScopeFilterSection;
